import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/Layout";
import { ArrowLeft } from "lucide-react";
import { format } from "date-fns";

interface AttendanceDetail {
  id: string;
  date: string;
  status: string;
  student_id?: string;
  instructor_id?: string;
  subject_id?: string;
  students?: {
    calling_name: string | null;
    office_name: string;
    first_name: string;
    last_name: string;
  };
  instructors?: {
    name: string;
  };
  subjects?: {
    name: string;
  };
}

const AttendanceDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date") || "";
  const type = searchParams.get("type") || "student";
  
  const [userName, setUserName] = useState("");
  const [attendance, setAttendance] = useState<AttendanceDetail[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchAttendanceDetails();
  }, [date, type]);

  const fetchAttendanceDetails = async () => {
    setLoading(true);
    try {
      if (type === "student") {
        const { data, error } = await supabase
          .from("student_attendance")
          .select(`
            id,
            date,
            status,
            student_id,
            instructor_id,
            subject_id,
            students (
              calling_name,
              office_name,
              first_name,
              last_name
            ),
            instructors (
              name
            ),
            subjects (
              name
            )
          `)
          .eq("date", date)
          .order("status", { ascending: false });

        if (error) throw error;
        setAttendance(data || []);
      } else {
        const { data, error } = await supabase
          .from("instructor_attendance")
          .select(`
            id,
            date,
            status,
            instructor_id,
            instructors (
              name
            )
          `)
          .eq("date", date)
          .order("status", { ascending: false });

        if (error) throw error;
        setAttendance(data || []);
      }
    } catch (error) {
      console.error("Error fetching attendance details:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variant = status === "Present" ? "default" : "destructive";
    return <Badge variant={variant}>{status}</Badge>;
  };

  return (
    <Layout userName={userName}>
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/attendance/calendar")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Calendar
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {type === "student" ? "Student" : "Instructor"} Attendance - {format(new Date(date), "MMMM dd, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : attendance.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No attendance records found for this date
              </div>
            ) : (
              <div className="space-y-2">
                {attendance.map((record) => (
                  <div
                    key={record.id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg hover:bg-accent transition-colors gap-2"
                  >
                    <div className="flex-1">
                      <div className="font-medium">
                        {type === "student" 
                          ? (record.students?.calling_name || record.students?.office_name || `${record.students?.first_name} ${record.students?.last_name}`)
                          : record.instructors?.name
                        }
                      </div>
                      {type === "student" && (
                        <div className="text-sm text-muted-foreground mt-1">
                          {record.instructors?.name && `Instructor: ${record.instructors.name}`}
                          {record.subjects?.name && ` • Subject: ${record.subjects.name}`}
                        </div>
                      )}
                    </div>
                    {getStatusBadge(record.status)}
                  </div>
                ))}
                
                <div className="mt-6 pt-6 border-t">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                      <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                        {attendance.filter(a => a.status === "Present").length}
                      </div>
                      <div className="text-sm text-muted-foreground">Present</div>
                    </div>
                    <div className="p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                      <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                        {attendance.filter(a => a.status === "Absent").length}
                      </div>
                      <div className="text-sm text-muted-foreground">Absent</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AttendanceDetails;